import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getSessionUser, onAuthChange, signOut } from './services/supabase';

import Header from './components/Header';
import Footer from './components/Footer';


import Home from './pages/Home';
import Services from './pages/Services';
import BookingPage from './pages/BookingPage';
import Showcase from './pages/Showcase';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminDebug from './pages/AdminDebug';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import QuickAuth from './pages/QuickAuth';
import PaymentPage from './pages/PaymentPage';
import Success from './pages/Success';
import AddOnSelection from './pages/AddOnSelection';
import './App.css';

function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<any | null>(null);
  const [profile, setProfile] = useState<any | null>(null);

  // Auth context value (exported below) allows other components to read profile/session.
  // Initialize session/profile on mount and subscribe to auth changes.
  useEffect(() => {
    let unsub: (() => void) | undefined;
    const init = async () => {
      setLoading(true);
      try {
        const { session: sess, profile: prof } = await getSessionUser();
        setSession(sess ?? null);
        setProfile(prof ?? null);
        // Primary: admin if profile.role === 'admin'
        if (prof?.role === 'admin') {
          setIsAdmin(true);
        } else {
          // Fallback: allow app-level admin password (local testing) via sessionStorage flag
          const fallback = sessionStorage.getItem('isAdmin');
          if (fallback === '1') {
            console.info('[App] sessionStorage admin flag found, enabling admin mode (local fallback)');
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        }

        // subscribe to auth changes and refresh profile when events occur
        unsub = onAuthChange(async (_event, sessData) => {
          const s = sessData?.session ?? null;
          setSession(s);
          try {
            const { profile: updated } = await getSessionUser();
            setProfile(updated ?? null);
            setIsAdmin(updated?.role === 'admin');
          } catch (err) {
            console.error('Error refreshing profile on auth change', err);
            setProfile(null);
            setIsAdmin(false);
          }
        });
      } catch (err) {
        console.error('Auth initialization error:', err);
        setSession(null);
        setProfile(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    // Listen for local admin flag events (dispatched by AdminLogin when using app-level admin password)
    const onAdminFlag = () => {
      console.info('[App] received app:admin-flag event; enabling admin mode from sessionStorage fallback');
      if (sessionStorage.getItem('isAdmin') === '1') setIsAdmin(true);
    };
    window.addEventListener('app:admin-flag', onAdminFlag);

    init();
    return () => {
      if (unsub) unsub();
      window.removeEventListener('app:admin-flag', onAdminFlag);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      setIsAdmin(false);
      setProfile(null);
      setSession(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <AuthContext.Provider value={{ session, profile, setProfile }}>
        <div className="App">
          <Header isAdmin={isAdmin} onLogout={handleLogout} />
          <main className="main-content">
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route 
              path="/admin/login" 
              element={
                isAdmin ? <Navigate to="/admin" replace /> : <AdminLogin />
              } 
            />
            <Route 
              path="/admin" 
              element={
                isAdmin ? <AdminDashboard /> : <Navigate to="/admin/login" replace />
              } 
            />
            <Route path="/admin/check" element={<AdminDebug />} />
            <Route path="/quick-auth" element={<QuickAuth />} />
            <Route path="/add-ons" element={<AddOnSelection />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/success" element={<Success />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </AuthContext.Provider>
    </Router>
  );
}

export default App;

// Simple AuthContext (import from other components when you need profile/session)
export const AuthContext = React.createContext<{
  session: any | null;
  profile: any | null;
  setProfile: (p: any) => void;
}>({ session: null, profile: null, setProfile: () => {} });
